<script lang="ts">
/**
 * LumiMarkdown — a small, sandboxed markdown-to-VNode renderer (issue 534).
 *
 * Built for Lumi's chat replies (assistant messages render markdown; a
 * person's own messages stay plain text, decided by the caller), but generic:
 * it takes a string and nothing else app-specific, so it is exported as a
 * plain display primitive.
 *
 * NO markdown library, NO `v-html`, NO raw HTML pass-through. Every node this
 * parser produces is a `h()` call with a TEXT child (`strong`/`em`/`code`
 * wrap a string, never markup), so a literal `<script>` or `<b>` in the
 * source can never become an element — Vue mounts it as a text node either
 * way. That is also why this file cannot "sanitize" anything: there is
 * nothing unsafe to sanitize once HTML is never parsed in the first place.
 *
 * Subset supported: paragraphs and single line breaks, **bold**, *italic*,
 * `inline code`, fenced ``` code blocks ``` (rendered verbatim, no inline
 * parsing inside), bullet (`- `/`* `) and numbered (`1. `) lists, `#`..`######`
 * headings (recognised anywhere a block boundary can start, not only at the
 * top of the message), `---`/`***`/`___` thematic breaks (hr), `>` blockquotes
 * (including an empty `>` line and a nested list/heading/etc. inside — a
 * blockquote's content is dequoted one level and fed back through
 * `parseBlocks`, so anything this parser can already do nests for free),
 * http(s) links (opened in a new tab, `rel="noopener"`), Markdown links
 * `[label](https://…)` and `[label](module/type:id)` (the latter read as the
 * entity-link token it means — issue 1101), backslash escapes, the suite's own
 * entity-link token `[[module/type:id|label]]` (mirrors `useLumiAssistant`'s
 * `parseLines` — duplicated rather than imported: this is a display-tier
 * primitive and does not reach into navigation-tier internals) and pipe
 * tables with a header row. Nothing else is recognised — an unclosed `**`, a
 * table missing its `---` separator row, an unterminated code fence, or any
 * other malformed input is never rejected: the block/inline scanners below
 * only ever promote a run of lines to a richer shape (list/table/bold/…) when
 * it unambiguously looks like one, and fall back to plain text lines
 * otherwise. Never throws.
 */
import { defineComponent, h, type PropType, type VNode, type VNodeChild } from 'vue';

/** A parsed entity-link token `[[module/type:id|label]]`. Structurally the
 *  same shape as `useLumiAssistant`'s `AssistantLinkRef` on purpose — a host
 *  wires its transport's `resolveLink`/`navigate` straight through. */
export interface LumiMarkdownLinkRef {
  module: string;
  type: string;
  id: string;
  label: string;
}

type InlineNode =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'italic'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'link'; href: string; label?: string }
  | { kind: 'entity-link'; ref: LumiMarkdownLinkRef };

type Block =
  | { type: 'paragraph'; lines: InlineNode[][] }
  | { type: 'list'; ordered: boolean; items: InlineNode[][] }
  | { type: 'table'; header: InlineNode[][]; rows: InlineNode[][][] }
  | { type: 'heading'; level: number; inline: InlineNode[] }
  | { type: 'hr' }
  | { type: 'blockquote'; blocks: Block[] }
  | { type: 'codeblock'; code: string; lang: string };

// Matches the namespaced `[[module/type:id|label]]` token and the legacy
// module-less `[[type:id|label]]` one, same grammar as useLumiAssistant.LINK_RE.
const ENTITY_LINK_RE = /^\[\[(?:([a-z0-9_-]+)\/)?([a-z0-9_-]+):([^|\]]+)\|([^\]]+)\]\]$/i;

// One alternation, tried left to right at every position: a backslash escape
// (`\*` is a literal asterisk, never emphasis), then bold, then italic (so
// "**x**" is never read as two adjacent italics), then code, then the
// entity-link token, then anything else in link brackets with a label (a
// token the model built itself, e.g. `[[ats/portal-settings|öffnen]]` — never a
// link, so only its label shows, not the brackets), then a Markdown link
// `[label](target)` — stray brackets around it included, the model's
// `[[label](ats/branding_preview:…)]` (issue 1101) — then a bare http(s) link.
// Every alternative requires its own closing delimiter on the SAME line, so an
// unclosed marker simply never matches here and falls through to plain text —
// see `parseInline`.
const INLINE_TOKEN_RE =
  /\\([!-/:-@[-`{-~])|(\*\*(?:(?!\*\*).)+\*\*)|(\*(?:(?!\*).)+\*)|(`(?:(?!`).)+`)|(\[\[(?:[a-z0-9_-]+\/)?[a-z0-9_-]+:[^|\]]+\|[^\]]+\]\])|\[\[[^[\]|]*\|([^[\]]+)\]\]|\[?\[([^[\]]+)\]\(([^()\s]+)\)\]?|(https?:\/\/[^\s<>]+)/gi;

// The target of a Markdown link that is an entity-link ref in disguise:
// `[label](ats/branding_preview:v2.…)` carries the same module/type:id as the
// `[[ats/branding_preview:v2.…|label]]` token it should have been. A URL
// scheme (`https:`, `mailto:`) is no type.
const LINK_TARGET_REF_RE = /^(?:([a-z0-9_-]+)\/)?([a-z0-9_-]+):([^|\]\s]+)$/i;
const URL_SCHEME_RE = /^(?:https?|mailto|tel|javascript|data|ftp|file)$/i;

function markdownLinkNode(label: string, target: string): InlineNode {
  const shown = label.replace(/\*\*|__/g, '').trim() || target;
  if (/^https?:\/\//i.test(target)) return { kind: 'link', href: target, label: shown };
  const m = LINK_TARGET_REF_RE.exec(target);
  if (m && !URL_SCHEME_RE.test(m[2]) && !m[3].startsWith('//')) {
    return { kind: 'entity-link', ref: { module: m[1] || '', type: m[2], id: m[3], label: shown } };
  }
  // Anything else (a bare path, an unknown scheme) is no link: only its label.
  return { kind: 'text', text: shown };
}

// A model writing "CommonMark-safe" text escapes the brackets and parentheses
// of its own links — `\[\[Label\](ats/…)\]` (issue 1101). In a chat reply a
// literal bracket is far rarer than a link, so those four escapes are undone
// before the scan (never inside a code span), and the link reads as one.
// A line-final backslash is CommonMark's hard line break; lines already break
// here, so it is dropped rather than shown.
function unescapeLinkPunctuation(line: string): string {
  return line
    .replace(/(`[^`]*`)|\\([[\]()])/g, (all, code: string | undefined, ch: string | undefined) => code ?? ch ?? all)
    .replace(/(^|[^\\])\\$/, '$1');
}

// A trailing `.`, `,`, `;`, `:`, `!`, `?` or `)` is sentence punctuation far
// more often than it is part of the URL — strip it so "see https://x.test."
// does not carry the period into the href.
function trimUrl(raw: string): string {
  return raw.replace(/[),.;:!?\]]+$/, '');
}

function parseInline(raw: string): InlineNode[] {
  const line = unescapeLinkPunctuation(raw);
  const nodes: InlineNode[] = [];
  let lastIndex = 0;
  INLINE_TOKEN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = INLINE_TOKEN_RE.exec(line)) !== null) {
    if (m.index > lastIndex) nodes.push({ kind: 'text', text: line.slice(lastIndex, m.index) });
    const [full, escaped, bold, italic, code, entityLink, bracketLabel, mdLabel, mdTarget, url] = m;
    if (escaped) {
      nodes.push({ kind: 'text', text: escaped });
    } else if (bold) {
      nodes.push({ kind: 'bold', text: bold.slice(2, -2) });
    } else if (italic) {
      nodes.push({ kind: 'italic', text: italic.slice(1, -1) });
    } else if (code) {
      nodes.push({ kind: 'code', text: code.slice(1, -1) });
    } else if (entityLink) {
      const ref = parseEntityLink(entityLink);
      nodes.push(ref ? { kind: 'entity-link', ref } : { kind: 'text', text: full });
    } else if (bracketLabel) {
      nodes.push({ kind: 'text', text: bracketLabel.trim() });
    } else if (mdLabel && mdTarget) {
      nodes.push(markdownLinkNode(mdLabel, mdTarget));
    } else if (url) {
      const trimmed = trimUrl(url);
      nodes.push({ kind: 'link', href: trimmed });
      if (trimmed.length < url.length) nodes.push({ kind: 'text', text: url.slice(trimmed.length) });
    }
    lastIndex = m.index + full.length;
    // None of the alternatives above can match an empty string, but this
    // keeps the scan provably terminating even if a future one did.
    if (INLINE_TOKEN_RE.lastIndex === m.index) INLINE_TOKEN_RE.lastIndex++;
  }
  if (lastIndex < line.length) nodes.push({ kind: 'text', text: line.slice(lastIndex) });
  if (nodes.length === 0) nodes.push({ kind: 'text', text: '' });
  return nodes;
}

function parseEntityLink(token: string): LumiMarkdownLinkRef | null {
  const m = ENTITY_LINK_RE.exec(token);
  if (!m) return null;
  return { module: m[1] || '', type: m[2], id: m[3], label: m[4].trim() };
}

function isSeparatorRow(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.includes('-')) return false;
  return /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?$/.test(trimmed);
}
function looksLikeTableRow(line: string): boolean {
  return line.includes('|');
}
function splitTableRow(line: string): string[] {
  let t = line.trim();
  if (t.startsWith('|')) t = t.slice(1);
  if (t.endsWith('|')) t = t.slice(0, -1);
  return t.split('|').map((cell) => cell.trim());
}
function listKind(line: string): 'bullet' | 'numbered' | null {
  if (/^\s*[-*]\s+/.test(line)) return 'bullet';
  if (/^\s*\d+\.\s+/.test(line)) return 'numbered';
  return null;
}
function stripListMarker(line: string): string {
  return line.replace(/^\s*[-*]\s+/, '').replace(/^\s*\d+\.\s+/, '');
}

// A thematic break: the WHOLE line (ignoring up to 3 leading/trailing spaces)
// is three or more of the same `-`/`*`/`_`, with nothing else on it. This is
// the common, unambiguous case (a bare `---` on its own line); it deliberately
// does not chase CommonMark's space-separated variant (`- - -`), which reads
// as a bullet run in this parser's simpler world anyway.
const HR_RE = /^ {0,3}([-*_])\1{2,}\s*$/;
function isHr(line: string): boolean {
  return HR_RE.test(line);
}

// ATX heading: 1-6 `#`, a required space, then the title. Recognised at any
// block boundary, not only at the top of the message — a heading appearing
// mid-paragraph still ends the paragraph and starts a heading block. A
// trailing run of `#` (the optional closing sequence, `## Done ##`) is
// stripped along with the whitespace before it.
const HEADING_RE = /^ {0,3}(#{1,6})\s+(.+?)\s*$/;
function headingMatch(line: string): { level: number; text: string } | null {
  const m = HEADING_RE.exec(line);
  if (!m) return null;
  return { level: m[1].length, text: m[2].replace(/\s+#+$/, '') };
}

// A blockquote line: up to 3 leading spaces, then `>`. Consumed lines are
// dequoted by stripping exactly one leading `>` (and one following space, if
// present) — an empty `> ` line dequotes to an empty string, so it behaves as
// a blank line to the recursive block parser rather than ending the quote,
// and a doubly-quoted line (`> > …`) dequotes to a line that still starts
// with `>`, so nesting falls out of the recursion for free.
const BLOCKQUOTE_RE = /^ {0,3}>/;
function isBlockquoteLine(line: string): boolean {
  return BLOCKQUOTE_RE.test(line);
}
function dequote(line: string): string {
  return line.replace(/^ {0,3}>\s?/, '');
}

// A fenced code block's opening line: 3+ backticks or tildes, optionally
// followed by a language tag. The closing fence must use the same character,
// repeated at least as many times, alone on its line — an unterminated fence
// just runs to the end of the message rather than throwing or being ignored.
const FENCE_OPEN_RE = /^ {0,3}(`{3,}|~{3,})\s*(\S*)\s*$/;
function fenceMatch(line: string): { marker: string; lang: string } | null {
  const m = FENCE_OPEN_RE.exec(line);
  if (!m) return null;
  return { marker: m[1], lang: m[2] || '' };
}
function isFenceClose(line: string, marker: string): boolean {
  const ch = marker[0]; // '`' or '~' — neither needs escaping in a regex
  const re = new RegExp(`^ {0,3}${ch}{${marker.length},}\\s*$`);
  return re.test(line);
}

/** Block-level scan: blank-line-separated paragraphs, contiguous same-kind
 *  list runs, and a table ONLY when its header is immediately followed by a
 *  syntactically valid separator row — a table missing (or misspelling) that
 *  row is not promoted, so it renders as an ordinary paragraph instead
 *  (the "ragged table falls back to plain text lines" sad path). */
function parseBlocks(content: string): Block[] {
  const rawLines = String(content ?? '').split('\n');
  const blocks: Block[] = [];
  let i = 0;
  while (i < rawLines.length) {
    const startI = i;
    const line = rawLines[i];
    if (line.trim() === '') {
      i++;
    } else if (fenceMatch(line)) {
      const { marker, lang } = fenceMatch(line)!;
      const code: string[] = [];
      let j = i + 1;
      while (j < rawLines.length && !isFenceClose(rawLines[j], marker)) {
        code.push(rawLines[j]);
        j++;
      }
      // j either points at the closing fence (skip it too) or ran off the end
      // of the message (unterminated fence — take everything as code).
      i = j < rawLines.length ? j + 1 : j;
      blocks.push({ type: 'codeblock', code: code.join('\n'), lang });
    } else if (isHr(line)) {
      blocks.push({ type: 'hr' });
      i++;
    } else if (headingMatch(line)) {
      const { level, text } = headingMatch(line)!;
      blocks.push({ type: 'heading', level, inline: parseInline(text) });
      i++;
    } else if (isBlockquoteLine(line)) {
      const quoted: string[] = [];
      let j = i;
      while (j < rawLines.length && isBlockquoteLine(rawLines[j])) {
        quoted.push(dequote(rawLines[j]));
        j++;
      }
      blocks.push({ type: 'blockquote', blocks: parseBlocks(quoted.join('\n')) });
      i = j;
    } else if (looksLikeTableRow(line) && i + 1 < rawLines.length && isSeparatorRow(rawLines[i + 1])) {
      const header = splitTableRow(line);
      const rows: string[][] = [];
      let j = i + 2;
      while (j < rawLines.length && rawLines[j].trim() !== '' && looksLikeTableRow(rawLines[j])) {
        rows.push(splitTableRow(rawLines[j]));
        j++;
      }
      blocks.push({ type: 'table', header: header.map(parseInline), rows: rows.map((r) => r.map(parseInline)) });
      i = j;
    } else if (listKind(line)) {
      const kind = listKind(line);
      const items: string[] = [];
      let j = i;
      while (j < rawLines.length && listKind(rawLines[j]) === kind) {
        items.push(stripListMarker(rawLines[j]));
        j++;
      }
      blocks.push({ type: 'list', ordered: kind === 'numbered', items: items.map(parseInline) });
      i = j;
    } else {
      const lines: string[] = [];
      let j = i;
      while (
        j < rawLines.length &&
        rawLines[j].trim() !== '' &&
        !listKind(rawLines[j]) &&
        !(looksLikeTableRow(rawLines[j]) && j + 1 < rawLines.length && isSeparatorRow(rawLines[j + 1])) &&
        !fenceMatch(rawLines[j]) &&
        !isHr(rawLines[j]) &&
        !headingMatch(rawLines[j]) &&
        !isBlockquoteLine(rawLines[j])
      ) {
        lines.push(rawLines[j]);
        j++;
      }
      blocks.push({ type: 'paragraph', lines: lines.map(parseInline) });
      i = j;
    }
    if (i === startI) i++; // never stall, whatever a branch above did or didn't consume
  }
  return blocks;
}

export default defineComponent({
  name: 'LumiMarkdown',
  props: {
    /** The text to render. */
    content: { type: String, required: true },
    /** Resolve an entity-link token to an in-app path; return null/omit to render its label as text. */
    resolveLink: { type: Function as PropType<(ref: LumiMarkdownLinkRef) => { path: string; moduleKey?: string } | null>, default: undefined },
    /** Called with a resolved entity-link's path (+ moduleKey) on click. */
    navigate: { type: Function as PropType<(path: string, moduleKey?: string) => void>, default: undefined },
  },
  setup(props) {
    function renderInline(nodes: InlineNode[]): VNodeChild[] {
      return nodes.map((n, i): VNodeChild => {
        switch (n.kind) {
          case 'text':
            return n.text;
          case 'bold':
            return h('strong', { key: i }, n.text);
          case 'italic':
            return h('em', { key: i }, n.text);
          case 'code':
            return h('code', { key: i, class: 'lumi-markdown__code' }, n.text);
          case 'link':
            return h('a', { key: i, href: n.href, target: '_blank', rel: 'noopener', class: 'lumi-markdown__link' }, n.label ?? n.href);
          case 'entity-link': {
            const resolved = props.resolveLink?.(n.ref) ?? null;
            if (!resolved) return n.ref.label;
            return h(
              'button',
              {
                key: i,
                type: 'button',
                class: 'lumi-markdown__link',
                title: n.ref.label,
                onClick: () => props.navigate?.(resolved.path, resolved.moduleKey),
              },
              n.ref.label,
            );
          }
        }
      });
    }

    function renderBlock(block: Block, key: number): VNode {
      if (block.type === 'list') {
        return h(
          block.ordered ? 'ol' : 'ul',
          { key, class: 'lumi-markdown__list' },
          block.items.map((item, i) => h('li', { key: i }, renderInline(item))),
        );
      }
      if (block.type === 'table') {
        return h('div', { key, class: 'lumi-markdown__table-wrap' }, [
          h('table', { class: 'lumi-markdown__table' }, [
            h('thead', {}, h('tr', {}, block.header.map((cell, i) => h('th', { key: i }, renderInline(cell))))),
            h(
              'tbody',
              {},
              block.rows.map((row, ri) => h('tr', { key: ri }, row.map((cell, ci) => h('td', { key: ci }, renderInline(cell))))),
            ),
          ]),
        ]);
      }
      if (block.type === 'heading') {
        return h(
          `h${block.level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6',
          { key, class: ['lumi-markdown__heading', `lumi-markdown__heading--${block.level}`] },
          renderInline(block.inline),
        );
      }
      if (block.type === 'hr') {
        return h('hr', { key, class: 'lumi-markdown__hr' });
      }
      if (block.type === 'blockquote') {
        return h(
          'blockquote',
          { key, class: 'lumi-markdown lumi-markdown__blockquote' },
          block.blocks.map((b, i) => renderBlock(b, i)),
        );
      }
      if (block.type === 'codeblock') {
        return h('pre', { key, class: 'lumi-markdown__pre' }, h('code', { class: 'lumi-markdown__code lumi-markdown__code--block' }, block.code));
      }
      // paragraph — lines within it join with a real line break, not a new block.
      const children: VNodeChild[] = [];
      block.lines.forEach((line, i) => {
        if (i > 0) children.push(h('br', { key: `br-${i}` }));
        children.push(...renderInline(line));
      });
      return h('p', { key, class: 'lumi-markdown__p' }, children);
    }

    return () => h(
      'div',
      { class: 'lumi-markdown' },
      parseBlocks(props.content ?? '').map((block, i) => renderBlock(block, i)),
    );
  },
});
</script>

<style>
/* Paragraphs/lists stack with the same rhythm the assistant panel already uses
   for its message lines; a lone paragraph (the common case) costs nothing. */
.lumi-markdown { display: flex; flex-direction: column; gap: var(--space-2); min-width: 0; }
/* A word longer than the line — a path, a URL, an address — breaks inside
   itself rather than widening the thread past the panel. */
.lumi-markdown__p { margin: 0; white-space: normal; overflow-wrap: anywhere; }
.lumi-markdown__list { margin: 0; padding-left: var(--space-5); display: flex; flex-direction: column; gap: var(--space-1); overflow-wrap: anywhere; }
.lumi-markdown__code {
  font-family: var(--font-mono);
  font-size: 0.9em;
  background: var(--surface-wash);
  border-radius: var(--radius-sm);
  padding: 1px 5px;
}
/* Same look as the entity links the panel already rendered via parseLines —
   heading colour, coral underline (spec §2) — kept local rather than reused
   from LumiAssistantPanel.vue's stylesheet, since this component is exported
   standalone and must not depend on another component's CSS being loaded. */
.lumi-markdown__link {
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-weight: var(--weight-medium);
  color: var(--text-heading);
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: var(--attention);
  text-decoration-thickness: 1.5px;
  text-underline-offset: 3px;
  word-break: break-word;
}
a.lumi-markdown__link { cursor: pointer; }
.lumi-markdown__link:hover { color: var(--attention-text); }
/* The table scrolls inside its own box; the panel around it never does. */
.lumi-markdown__table-wrap { overflow-x: auto; max-width: 100%; }
.lumi-markdown__table { border-collapse: collapse; font-size: var(--text-sm); width: max-content; min-width: 100%; }
.lumi-markdown__table th, .lumi-markdown__table td {
  border: 1px solid var(--border-default);
  padding: var(--space-1) var(--space-2);
  text-align: left;
  vertical-align: top;
}
.lumi-markdown__table th {
  font-size: var(--text-caps);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--text-tertiary);
  background: var(--surface-wash);
  white-space: nowrap;
}
.lumi-markdown__heading {
  margin: 0;
  color: var(--text-heading);
  font-weight: var(--weight-display);
  line-height: var(--leading-snug);
}
.lumi-markdown__heading--1 { font-size: var(--text-xl); letter-spacing: var(--tracking-display); }
.lumi-markdown__heading--2 { font-size: var(--text-lg); letter-spacing: var(--tracking-display); }
.lumi-markdown__heading--3 { font-size: var(--text-base); font-weight: var(--weight-semibold); }
.lumi-markdown__heading--4 { font-size: var(--text-sm); font-weight: var(--weight-semibold); }
/* Thematic break — a hairline, same weight as every other structural border. */
.lumi-markdown__hr {
  border: none;
  border-top: var(--border-w) solid var(--border-default);
  margin: var(--space-1) 0;
}
/* A quoted block reuses `.lumi-markdown`'s own stacking rhythm for whatever
   nests inside it (paragraphs, a list, even another quote), so it never needs
   its own gap rules. */
.lumi-markdown__blockquote {
  margin: 0;
  padding: var(--space-1) var(--space-4);
  border-left: 3px solid var(--border-default);
  color: var(--text-tertiary);
}
.lumi-markdown__pre {
  margin: 0;
  overflow-x: auto;
  background: var(--surface-wash);
  border-radius: var(--radius-sm);
  padding: var(--space-2) var(--space-3);
}
.lumi-markdown__code--block {
  display: block;
  padding: 0;
  background: none;
  white-space: pre;
}
</style>
